import React, { useEffect, useState, useCallback } from "react";
import { api, errMsg } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { StatusBadge } from "@/components/shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Plus, Building2, Store } from "lucide-react";
import { toast } from "sonner";

const TYPE_LABEL = { main: "Main store", branch: "Branch" };

export default function AdminStoresPage() {
  const { refreshBoot } = useApp();
  const [stores, setStores] = useState([]);
  const [createOpen, setCreateOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ name: "", code: "", type: "branch" });
  const [editing, setEditing] = useState(null);

  const load = useCallback(() => {
    api.get("/stores").then((r) => setStores(r.data.stores)).catch((e) => toast.error(errMsg(e)));
  }, []);
  useEffect(() => { load(); }, [load]);

  const create = async () => {
    if (!form.name.trim() || !form.code.trim()) { toast.error("Name and code are required"); return; }
    setSaving(true);
    try {
      await api.post("/stores", form);
      toast.success(`${form.name.trim()} added`);
      setCreateOpen(false);
      setForm({ name: "", code: "", type: "branch" });
      load();
      refreshBoot();
    } catch (e) { toast.error(errMsg(e)); } finally { setSaving(false); }
  };

  const saveEdit = async () => {
    setSaving(true);
    try {
      await api.patch(`/stores/${editing.id}`, {
        name: editing.name, type: editing.type, active: editing.active,
      });
      toast.success("Store updated");
      setEditing(null);
      load();
      refreshBoot();
    } catch (e) { toast.error(errMsg(e)); } finally { setSaving(false); }
  };

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h1 className="font-display text-xl font-semibold arch-underline">Stores</h1>
          <p className="text-sm text-muted-foreground">Add branches, rename them, or deactivate — past entries stay intact and viewable</p>
        </div>
        <Button onClick={() => setCreateOpen(true)} data-testid="create-store-button"><Plus className="h-4 w-4 mr-1" />New store</Button>
      </div>

      <Card className="rounded-lg overflow-hidden">
        <Table className="table-compact">
          <TableHeader><TableRow>
            <TableHead>Name</TableHead><TableHead>Code</TableHead><TableHead>Type</TableHead>
            <TableHead>Status</TableHead><TableHead className="w-[80px]"></TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {stores.length === 0 && (
              <TableRow><TableCell colSpan={5} className="text-center text-sm text-muted-foreground py-8" data-testid="stores-empty">No stores yet — add your first one</TableCell></TableRow>
            )}
            {stores.map((s) => (
              <TableRow key={s.id} className={s.active === false ? "opacity-50" : ""} data-testid={`store-row-${s.code}`}>
                <TableCell className="font-medium flex items-center gap-2">
                  {s.type === "main" ? <Building2 className="h-3.5 w-3.5 text-[hsl(var(--brass))]" /> : <Store className="h-3.5 w-3.5 text-muted-foreground" />}
                  {s.name}
                </TableCell>
                <TableCell className="font-mono-num text-xs">{s.code}</TableCell>
                <TableCell className="text-xs">{TYPE_LABEL[s.type] || s.type}</TableCell>
                <TableCell><StatusBadge status={s.active === false ? "void" : "active"} label={s.active === false ? "Inactive" : "Active"} /></TableCell>
                <TableCell>
                  <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => setEditing({ ...s, active: s.active !== false })} data-testid={`store-edit-${s.code}`}>Edit</Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>

      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>New store</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1"><Label>Store name</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g. Chandni Chowk Branch" data-testid="create-store-name" /></div>
            <div className="grid grid-cols-2 gap-2">
              <div className="space-y-1"><Label>Short code</Label>
                <Input value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase().replace(/\s+/g, "") })} placeholder="BR3" className="font-mono-num" data-testid="create-store-code" /></div>
              <div className="space-y-1"><Label>Type</Label>
                <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                  <SelectTrigger data-testid="create-store-type"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="branch">Branch</SelectItem>
                    <SelectItem value="main">Main store</SelectItem>
                  </SelectContent>
                </Select></div>
            </div>
            <p className="text-xs text-muted-foreground">The code shows up on registers and print-outs. Assign cashiers to this store from Users &amp; permissions.</p>
          </div>
          <DialogFooter>
            <Button onClick={create} disabled={saving} data-testid="create-store-submit">{saving ? "Adding…" : "Add store"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Edit {editing?.code}</DialogTitle></DialogHeader>
          {editing && (
            <div className="space-y-3">
              <div className="space-y-1"><Label>Store name</Label>
                <Input value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} data-testid="edit-store-name" /></div>
              <div className="space-y-1"><Label>Type</Label>
                <Select value={editing.type} onValueChange={(v) => setEditing({ ...editing, type: v })}>
                  <SelectTrigger data-testid="edit-store-type"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="branch">Branch</SelectItem>
                    <SelectItem value="main">Main store</SelectItem>
                  </SelectContent>
                </Select></div>
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <Checkbox checked={editing.active} onCheckedChange={(v) => setEditing({ ...editing, active: !!v })} data-testid="edit-store-active" />
                Active
              </label>
              <p className="text-xs text-muted-foreground">Deactivating stops new day-books for this store. Old bills, registers and reports stay viewable.</p>
            </div>
          )}
          <DialogFooter>
            <Button onClick={saveEdit} disabled={saving} data-testid="edit-store-save">{saving ? "Saving…" : "Save"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
